import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormHelperText
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { validateFile } from "../../utils/encryption";

const FileUpload = ({ 
  onFileUpload, 
  fileFormat, 
  setFileFormat, 
  fileName 
}) => {
  const [error, setError] = useState("");
  
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validation = validateFile(file);
        if (!validation.isValid) {
          setError(validation.error);
          return;
        }
        
        setError("");
        onFileUpload(file);
      }
    },
    [onFileUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'video/mp4': ['.mp4'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/html': ['.html'],
      'application/json': ['.json']
    },
    maxFiles: 1
  });

  const handleFileFormatChange = (e) => {
    setFileFormat(e.target.value);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Upload a File to Encrypt
      </Typography>
      
      <Box mb={3}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="file-format-label">File Format</InputLabel>
          <Select
            labelId="file-format-label"
            id="file-format"
            value={fileFormat}
            label="File Format"
            onChange={handleFileFormatChange}
          >
            <MenuItem value="PNG">PNG</MenuItem>
            <MenuItem value="JPG">JPG</MenuItem>
            <MenuItem value="JPEG">JPEG</MenuItem>
            <MenuItem value="GIF">GIF</MenuItem>
            <MenuItem value="MP4">MP4</MenuItem>
            <MenuItem value="PDF">PDF</MenuItem>
            <MenuItem value="TXT">TXT</MenuItem>
            <MenuItem value="MD">MD</MenuItem>
            <MenuItem value="DOC">DOC</MenuItem>
            <MenuItem value="DOCX">DOCX</MenuItem>
            <MenuItem value="HTML">HTML</MenuItem>
            <MenuItem value="JSON">JSON</MenuItem>
          </Select>
          <FormHelperText>Select the format of the file you will upload</FormHelperText>
        </FormControl>
        
        <Button
          variant="contained"
          component="label"
          startIcon={<CloudUploadIcon />}
          fullWidth
          sx={{ mb: 2 }}
        >
          Browse Files
          <input
            type="file"
            hidden
            accept=".png,.jpg,.jpeg,.gif,.mp4,.pdf,.txt,.md,.doc,.docx,.html,.json"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                const validation = validateFile(file);
                if (!validation.isValid) {
                  setError(validation.error);
                  return;
                }
                
                setError("");
                onFileUpload(file);
              }
            }}
          />
        </Button>
        
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.400',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: isDragActive ? 'rgba(55, 91, 210, 0.05)' : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'rgba(55, 91, 210, 0.05)',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="body1" gutterBottom>
            {isDragActive
              ? "Drop the file here..."
              : "Drag and drop a file here, or click to select a file"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Supported formats: PNG, JPG, JPEG, GIF, MP4, PDF, TXT, MD, DOC, DOCX, HTML, JSON
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {fileName && !error && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Selected file: {fileName}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default FileUpload;